/**
 * Interrogation du réseau routier affiché, pour l'accrochage de position.
 *
 * Les géométries viennent des tuiles vectorielles déjà chargées : aucune
 * requête réseau supplémentaire, et l'accrochage fonctionne donc hors ligne
 * tant que les tuiles sont en cache.
 */

import type { Map as MapLibreMap } from 'maplibre-gl'

import { ROAD_LAYERS, ROAD_QUERY_RADIUS_PX, type RoadQuery } from '../core/snapToRoad'
import type { LatLng } from '../core/geo'

/**
 * Construit la fonction d'interrogation adossée à la carte.
 *
 * Les couches sont filtrées contre le style courant à chaque appel :
 * MapLibre GL JS **lève une erreur** sur une couche inconnue, alors que le SDK
 * Android les ignore, et la liste couvre volontairement deux schémas de tuiles
 * dont un seul est présent à la fois.
 */
export function createMapRoadQuery(map: MapLibreMap): RoadQuery {
  let cachedStyleLayers: string[] = []
  let cachedSignature: string | null = null

  const availableLayers = (): string[] => {
    const layers = map.getStyle()?.layers ?? []

    // Le filtrage n'est refait qu'au changement de style : il coûte un
    // parcours de la centaine de couches d'un fond de carte, à chaque relevé.
    //
    // La signature comprend le nom de la première couche et pas seulement leur
    // nombre : deux fonds de carte de schémas différents peuvent en compter
    // autant, et la liste filtrée serait alors celle du style précédent — donc
    // vide, et l'accrochage au réseau routier muet.
    const signature = `${layers.length}:${layers[0]?.id ?? ''}`

    if (signature !== cachedSignature) {
      const present = new Set(layers.map((layer) => layer.id))
      cachedStyleLayers = ROAD_LAYERS.filter((id) => present.has(id))
      cachedSignature = signature
    }

    return cachedStyleLayers
  }

  return (lat: number, lon: number): readonly (readonly LatLng[])[] => {
    if (!map.isStyleLoaded()) return []

    const layers = availableLayers()
    if (layers.length === 0) return []

    const point = map.project([lon, lat])
    const radius = ROAD_QUERY_RADIUS_PX

    let features
    try {
      features = map.queryRenderedFeatures(
        [
          [point.x - radius, point.y - radius],
          [point.x + radius, point.y + radius],
        ],
        { layers },
      )
    } catch {
      // Style en cours de rechargement : on saute ce relevé plutôt que de
      // faire échouer la boucle de navigation.
      return []
    }

    const lines: LatLng[][] = []

    for (const feature of features) {
      const geometry = feature.geometry

      if (geometry.type === 'LineString') {
        lines.push(toLatLngs(geometry.coordinates))
      } else if (geometry.type === 'MultiLineString') {
        for (const line of geometry.coordinates) {
          lines.push(toLatLngs(line))
        }
      }
    }

    return lines
  }
}

function toLatLngs(coordinates: readonly number[][]): LatLng[] {
  const result: LatLng[] = []

  for (const coordinate of coordinates) {
    const lon = coordinate[0]
    const lat = coordinate[1]
    if (typeof lon === 'number' && typeof lat === 'number') {
      result.push({ lat, lon })
    }
  }

  return result
}
